import { wmtsBase } from "./endpoints.ts";

export type BaseLayer = "map" | "aerial";

export interface WmtsLayer {
  layer: string;
  ext: "jpeg" | "png";
  /** Highest zoom swisstopo serves natively; Leaflet over-scales beyond it up to MAX_ZOOM. */
  maxNativeZoom: number;
}

export const MAX_ZOOM = 19;

/** The position pickers zoom a few levels past the native tiles (upscaled) for precise placement. */
export const PICKER_MAX_ZOOM = 22;

/** [[south, west], [north, east]] */
export type Bounds = [[number, number], [number, number]];

export const BASE_LAYERS: Record<BaseLayer, WmtsLayer> = {
  // maxNativeZoom here = the deepest level the offline saver stores (z18 / z19). The overview map uses it,
  // so an area saved for offline use keeps showing tiles beyond it by upscaling (never blank).
  map: { layer: "ch.swisstopo.pixelkarte-farbe", ext: "jpeg", maxNativeZoom: 18 },
  aerial: { layer: "ch.swisstopo.swissimage", ext: "jpeg", maxNativeZoom: 19 },
};

/**
 * The deepest level swisstopo really serves: the last matrix of each layer's TileMatrixSet in the WMTS
 * capabilities (3857_19 / 3857_20). Only the staff position pickers request it (online work); beyond it
 * Leaflet upscales instead of requesting tiles that do not exist.
 */
export const SERVED_NATIVE_ZOOM: Record<BaseLayer, number> = { map: 19, aerial: 20 };

export type TileContext = "overview" | "picker";

/** Native zoom for a layer in a context: the overview stays within what can be saved offline. */
export const nativeZoomFor = (layer: BaseLayer, context: TileContext): number =>
  context === "picker" ? SERVED_NATIVE_ZOOM[layer] : BASE_LAYERS[layer].maxNativeZoom;

/** Bathymetric relief shading, drawn over either base layer. */
/** The swisstopo relief tiles are colour-coded; we only show them from this zoom, desaturated. */
export const BATHY_MIN_ZOOM = 12;

export const BATHY_OVERLAY: WmtsLayer = {
  layer: "ch.swisstopo.swissbathy3d-reliefschattierung",
  ext: "png",
  maxNativeZoom: 17,
};

/** Leaflet still fills {z}/{x}/{y}; layer and extension are fixed per layer. */
export function wmtsUrl({ layer, ext }: Pick<WmtsLayer, "layer" | "ext">): string {
  return `${wmtsBase()}/1.0.0/${layer}/default/current/3857/{z}/{x}/{y}.${ext}`;
}

/** The concrete URL Leaflet requests for one tile: the cache key of that tile, offline or not. */
export function wmtsTileUrl(
  spec: Pick<WmtsLayer, "layer" | "ext">,
  z: number,
  x: number,
  y: number,
): string {
  return wmtsUrl(spec)
    .replace("{z}", String(z))
    .replace("{x}", String(x))
    .replace("{y}", String(y));
}

/**
 * Extent of the swisstopo WMTS layers we use (pixelkarte-farbe, swissimage and the bathy relief share it):
 * the `ows:WGS84BoundingBox` of each layer in
 * https://wmts.geo.admin.ch/EPSG/3857/1.0.0/WMTSCapabilities.xml, 5.140242 45.398181 .. 11.47757 48.230651
 * (lon lat). Tiles do not exist outside this box; the map never shows or pans beyond it.
 * [[south, west], [north, east]]
 */
export const COVERAGE_BOUNDS: Bounds = [
  [45.398181, 5.140242],
  [48.230651, 11.47757],
];

/** Roughly Switzerland: what the overview shows at first. */
export const SWITZERLAND_BOUNDS: Bounds = [
  [45.78, 5.9],
  [47.85, 10.55],
];
